import {
  Box,
  Center,
  Divider,
  Spinner,
  Stack,
  StackDivider,
  Text,
} from '@chakra-ui/react';
import * as React from 'react';

import { Loading } from '@/imports/ui/chakra/components/Loading';

import { SponsorDirectoryCompanyHit } from '../SponsorDirectoryCompanyHit';
import { SponsorDirectory_Search_sponsorDirectorySearch_results } from '../../graphql-types/SponsorDirectory_Search';

export const Hits: React.FC<{
  sponsors: SponsorDirectory_Search_sponsorDirectorySearch_results[];
  activeTab: 'following' | 'explore';
  loading: boolean;
  isFollowingBadge?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}> = ({
  sponsors,
  activeTab,
  loading,
  isFollowingBadge,
  hasMore,
  isLoadingMore,
  onLoadMore,
}) => {
  const isLoadingMoreRef = React.useRef(false);
  const observerRef = React.useRef<IntersectionObserver | null>(null);

  React.useEffect(() => {
    if (!isLoadingMore) {
      isLoadingMoreRef.current = false;
    }
  }, [isLoadingMore, sponsors.length]);

  const sentinelRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node || !hasMore || !onLoadMore) {
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (
            !entry.isIntersecting ||
            isLoadingMoreRef.current ||
            isLoadingMore
          ) {
            return;
          }

          isLoadingMoreRef.current = true;
          onLoadMore();
        },
        {
          rootMargin: '250px',
        }
      );

      observer.observe(node);
      observerRef.current = observer;
    },
    [hasMore, isLoadingMore, onLoadMore]
  );

  if (loading && sponsors.length === 0) {
    return <Loading />;
  }

  if (sponsors.length === 0) {
    return (
      <Text color="gray.600" px="2">
        {activeTab === 'following'
          ? 'You are not following any sponsors yet.'
          : ''}
      </Text>
    );
  }

  return (
    <>
      {!!sponsors.length && <Divider my="4" />}
      <Stack spacing="6" divider={<StackDivider />}>
        {sponsors.map((sponsor) => (
          <SponsorDirectoryCompanyHit
            key={sponsor._id}
            sponsor={sponsor}
            isFollowing={isFollowingBadge && (sponsor.isFollowing ?? false)}
          />
        ))}
      </Stack>

      {hasMore && <Box ref={sentinelRef} minH="1px" />}

      {hasMore && isLoadingMore && (
        <Center py="6">
          <Spinner />
        </Center>
      )}
    </>
  );
};
